import { useRef, useState } from 'react';
import { analyzeGame, type AnalyzeGameOptions } from '../../lib/coaching/analyzeGame';
import { runForeground } from '../../lib/engine/engineGate';
import { useEngine } from '../../hooks/useEngine';
import { MomentExplorer } from './MomentExplorer';
import type { CoachingReport, Color, ParsedGame } from '../../types';

export interface AnalysisPanelProps {
  game: ParsedGame;
  playerColor: Color;
  existingReport: CoachingReport | null;
  onReportReady: (report: CoachingReport) => void;
}

type Phase = 'idle' | 'loading-engine' | 'analyzing' | 'done' | 'error' | 'cancelled';

export function AnalysisPanel({ game, playerColor, existingReport, onReportReady }: AnalysisPanelProps) {
  const { engine, status: engineStatus } = useEngine();
  const [phase, setPhase] = useState<Phase>(existingReport ? 'done' : 'idle');
  const [progress, setProgress] = useState<{ phase: string; current: number; total: number } | null>(null);
  const [report, setReport] = useState<CoachingReport | null>(existingReport);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  async function runAnalysis() {
    setErrorMessage(null);
    setPhase('loading-engine');
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      // Coupe court à toute analyse d'arrière-plan en cours (synchro
      // Chess.com) : une demande explicite de l'utilisateur a toujours
      // priorité sur la file (cf. lib/engine/engineGate.ts).
      const result = await runForeground(async () => {
        await engine.init();
        setPhase('analyzing');
        const options: AnalyzeGameOptions = {
          signal: controller.signal,
          onProgress: (p) => setProgress(p),
        };
        return analyzeGame(game, playerColor, engine, options);
      });
      setReport(result);
      setPhase('done');
      onReportReady(result);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setPhase('cancelled');
      } else {
        setPhase('error');
        setErrorMessage(describeEngineError(err));
      }
    }
  }

  function cancelAnalysis() {
    abortRef.current?.abort();
  }

  return (
    <section className="panel">
      <h2>Analyse pédagogique</h2>

      {phase === 'idle' && (
        <div>
          <p className="muted">
            Magnus va analyser ta partie avec Stockfish (dans ton navigateur, rien n'est envoyé sur un serveur) et
            retenir au maximum deux moments clés compréhensibles à ton niveau.
          </p>
          <button type="button" className="primary-btn" onClick={runAnalysis}>
            Analyser cette partie
          </button>
        </div>
      )}

      {(phase === 'loading-engine' || phase === 'analyzing') && (
        <div className="progress-block" role="status" aria-live="polite">
          <p>
            {phase === 'loading-engine'
              ? 'Chargement du moteur Stockfish (WASM)…'
              : progress
                ? `Analyse en cours (${progress.phase === 'screening' ? 'balayage' : 'confirmation'}) — ${progress.current}/${progress.total}`
                : 'Analyse en cours…'}
          </p>
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{ width: progress ? `${Math.min(100, (progress.current / Math.max(1, progress.total)) * 100)}%` : '10%' }}
            />
          </div>
          <button type="button" className="link-btn" onClick={cancelAnalysis}>
            Annuler
          </button>
        </div>
      )}

      {phase === 'cancelled' && (
        <div>
          <p className="muted">Analyse annulée.</p>
          <button type="button" className="primary-btn" onClick={runAnalysis}>
            Relancer l'analyse
          </button>
        </div>
      )}

      {phase === 'error' && (
        <div className="error-block" role="alert">
          <p>{errorMessage}</p>
          <button type="button" className="primary-btn" onClick={runAnalysis}>
            Réessayer
          </button>
        </div>
      )}

      {phase === 'done' && report && (
        <div>
          {report.notes.length > 0 && (
            <ul className="notes-list">
              {report.notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          )}
          {report.moments.length === 0 ? (
            <p className="muted">Aucun moment clé à afficher pour cette partie.</p>
          ) : (
            <div className="moments-list">
              {report.moments.map((m, i) => (
                <MomentExplorer key={m.id} moment={m} index={i} />
              ))}
            </div>
          )}
          <button type="button" className="link-btn" onClick={runAnalysis}>
            Relancer l'analyse
          </button>
        </div>
      )}

      <p className="muted engine-status-line">Statut du moteur : {engineStatus}</p>
    </section>
  );
}

function describeEngineError(err: unknown): string {
  if (err instanceof Error) {
    return `L'analyse a échoué : ${err.message}`;
  }
  return "L'analyse a échoué pour une raison inconnue. Réessaie, ou recharge la page si le problème persiste.";
}
